import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Datepicker, Input, useLocale } from 'shared-ui';

import { ReactComponent as ReportInneEventsIcon } from 'assets/icons/reports.svg';
import { getNow } from 'helpers/getNow';
import { handleValidFromDate } from 'helpers/handleValidFromDate';
import { handleValidToDate } from 'helpers/handleValidToDate';
import { useAppSelectOptions } from 'hooks/useAppSelectOptions/useAppSelectOptions';
import { usePermissions } from 'hooks/usePermissions/usePermissions';
import { AppMessages } from 'i18n/messages';
import { Form } from 'ui/form/Form';
import { useRenderMultiAutocompleteOption } from 'ui/multiAutocomplete/hooks/useRenderMultiAutocompleteOption';
import { MultiAutocomplete } from 'ui/multiAutocomplete/MultiAutocomplete';
import { ReportErrorAlert } from '../reportErrorAlert';
import { ReportModal } from '../reportModal/ReportModal';
import { ReportTile } from '../reportTile/ReportTile';

import { InneEventsReportForm, InneEventsReportProps } from './InneEventsReport.types';

export const InneEventsReport = ({
  onModalOpen,
  isModalOpen,
  onModalClose,
  servers,
  terminals,
  isLoadingTerminals,
  onDownload,
  isLoadingDownload,
  hasNoDataError,
  hasNetworkError,
  onEmailModalOpen,
  isEmailModalOpen,
  isSmtpConfigured,
  onResetError,
}: InneEventsReportProps) => {
  const { formatMessage } = useLocale();
  const { perms } = usePermissions();

  const {
    control,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useFormContext<InneEventsReportForm>();

  const { categoryOptions, serverOptions } = useAppSelectOptions({ servers });
  const { renderMultiAutocompleteOption } = useRenderMultiAutocompleteOption();

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (hasNoDataError && name) {
        onResetError();
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, hasNoDataError, onResetError]);

  const terminalsOptions = terminals.map((terminal) => ({
    label: terminal.nazwa,
    value: terminal.id,
    number: terminal.numerLogicznyKontrolera,
  }));

  return (
    <>
      {perms?.RAPORT_ZDARZEN_INNE?.view && (
        <ReportTile
          icon={<ReportInneEventsIcon />}
          name={
            <>
              Raport
              <br />
              zdarzeń - inne
            </>
          }
          onClick={onModalOpen}
        />
      )}

      <ReportModal
        header={formatMessage({ id: AppMessages['reports.inneEventsReport.title'] })}
        onClose={onModalClose}
        open={isModalOpen}
        onSend={onEmailModalOpen}
        onDownload={() => onDownload()}
        isLoading={isLoadingDownload}
        isSendButtonDisabled={isLoadingDownload || !isSmtpConfigured || Object.keys(errors).length > 0}
        isDownloadButtonDisabled={isLoadingDownload || Object.keys(errors).length > 0}
      >
        <Form.Grid gridTemplateColumns="1fr 1fr">
          <Controller
            name="dataOd"
            control={control}
            render={({ field }) => {
              const dataDo = watch('dataDo');
              const maxAllowed = dataDo || new Date();

              return (
                <Datepicker
                  {...field}
                  onChange={(date) => {
                    field.onChange(date);
                    trigger('dataDo');
                  }}
                  label={formatMessage({ id: AppMessages['common.from'] })}
                  placeholderText={formatMessage({ id: AppMessages['reports.inneEventsReport.fromDate.placeholder'] })}
                  withCalendarIcon
                  withDatepickerPopover
                  isClickable
                  maxDate={maxAllowed}
                  errorMessage={errors?.dataOd?.message}
                  popoverOrigin={{
                    vertical: -80,
                    horizontal: 60,
                  }}
                  mediumPlaceholderFontSize
                  customSetDateFormat={handleValidFromDate}
                  customDateFormatOnOpen={() => field.value || getNow(dataDo)}
                  withoutMinDateError
                />
              );
            }}
          />

          <Controller
            name="dataDo"
            control={control}
            render={({ field }) => {
              const dataOd = watch('dataOd');

              return (
                <Datepicker
                  {...field}
                  onChange={(date) => {
                    field.onChange(date);
                    trigger('dataOd');
                  }}
                  label={formatMessage({ id: AppMessages['common.to'] })}
                  placeholderText={formatMessage({ id: AppMessages['reports.inneEventsReport.toDate.placeholder'] })}
                  minDate={dataOd || undefined}
                  maxDate={new Date()}
                  withCalendarIcon
                  withDatepickerPopover
                  isClickable
                  errorMessage={errors?.dataDo?.message}
                  popoverOrigin={{
                    vertical: -80,
                    horizontal: 60,
                  }}
                  mediumPlaceholderFontSize
                  customSetDateFormat={handleValidToDate}
                  customDateFormatOnOpen={() => field.value || getNow(dataOd)}
                  withoutMinDateError
                />
              );
            }}
          />

          <Controller
            name="numerKarty"
            control={control}
            render={({ field }) => (
              <Input
                label={formatMessage({ id: AppMessages['reports.inneEventsReport.cardNumber'] })}
                placeholder={formatMessage({ id: AppMessages['reports.inneEventsReport.cardNumber.placeholder'] })}
                {...field}
              />
            )}
          />

          <Form.ControlledSelect
            name="kategoria"
            control={control}
            label={formatMessage({ id: AppMessages['reports.inneEventsReport.category'] })}
            placeholder={formatMessage({ id: AppMessages['reports.inneEventsReport.category.placeholder'] })}
            withHelperText
            options={categoryOptions}
          />

          <Form.ControlledSelect
            name="serwerId"
            control={control}
            onChange={(e) => {
              setValue('serwerId', e.target.value as string, { shouldDirty: true });
              setValue('tkds', []);
            }}
            label={formatMessage({ id: AppMessages['reports.inneEventsReport.server'] })}
            placeholder={formatMessage({ id: AppMessages['reports.inneEventsReport.server.placeholder'] })}
            withHelperText
            options={serverOptions}
          />

          <Controller
            name="tkds"
            control={control}
            render={({ field }) => (
              <MultiAutocomplete
                {...field}
                withLimitedTags
                stopRotatePopupIndicator
                inputSize="big"
                limitTags={1}
                onChange={(_, option) => (option ? field.onChange(option) : field.onChange(null))}
                options={terminalsOptions}
                loading={isLoadingTerminals}
                label={formatMessage({ id: AppMessages['reports.inneEventsReport.reader'] })}
                placeholder={formatMessage({ id: AppMessages['reports.inneEventsReport.reader.placeholder'] })}
                renderOption={renderMultiAutocompleteOption}
              />
            )}
          />
        </Form.Grid>

        {hasNoDataError && !isEmailModalOpen && (
          <ReportErrorAlert message={formatMessage({ id: AppMessages['reports.error.noData'] })} />
        )}

        {hasNetworkError && !isEmailModalOpen && (
          <ReportErrorAlert message={formatMessage({ id: AppMessages['reports.error.serverError'] })} />
        )}
      </ReportModal>
    </>
  );
};
